import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  authService,
  useAuthStore,
  loginWithEmail as firebaseLogin,
  onAuthChange,
  firebaseLogout,
  getIdToken,
  getCurrentFirebaseUser,
  sendCurrentUserEmailVerification,
  getPortalUrls,
  apiErrorDetail,
} from '@lipaflow/shared'
import { AuthShell, AuthError, Button, Card, Input } from '@lipaflow/ui'
import {
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  BuildingLibraryIcon,
  ClipboardDocumentCheckIcon,
  FingerPrintIcon,
} from '@heroicons/react/24/outline'

/**
 * Admin console sign-in. Auth logic is intentionally unchanged from the
 * previous implementation — the rewrite is presentation-only, replacing
 * bespoke inputs/buttons with the shared @lipaflow/ui primitives.
 */

const CONSOLE_HIGHLIGHTS = [
  {
    icon: BuildingLibraryIcon,
    title: 'Customer operations',
    description: 'Onboard, verify and support every institution from one place.',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'Verification queue',
    description: 'Review sign-ups and keep the platform trusted.',
  },
  {
    icon: FingerPrintIcon,
    title: 'Full audit trail',
    description: 'Every sensitive action is logged and reviewable.',
  },
]

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const { setFirebaseAuth, setUser, setTenant, logout } = useAuthStore()
  const isCompletingAuthRef = useRef(false)
  const portalUrls = getPortalUrls()

  const finalizeAdminSession = async (uid: string): Promise<void> => {
    if (isCompletingAuthRef.current) return
    isCompletingAuthRef.current = true
    setFirebaseAuth(uid)

    try {
      const firebaseUser = getCurrentFirebaseUser()
      if (firebaseUser?.email && !firebaseUser.emailVerified) {
        await sendCurrentUserEmailVerification({
          actionUrl: `${window.location.origin}/login?verified=1`,
        })
        throw new Error(
          'Verify your email before signing in. We sent a verification link to your inbox.',
        )
      }

      const token = await getIdToken(true)
      if (!token) {
        throw new Error('Could not refresh your sign-in session. Please try again.')
      }

      const user = await authService.getCurrentUser()
      if (!user.permissions?.includes('platform:admin')) {
        throw new Error('Access denied. Super Admin credentials required.')
      }

      setUser(user)
      setTenant(null)
      navigate('/')
    } catch (err) {
      logout()
      await firebaseLogout()
      throw err
    } finally {
      isCompletingAuthRef.current = false
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          await finalizeAdminSession(firebaseUser.uid)
        } catch (err: unknown) {
          const authErr = err as { message?: string }
          setError(authErr.message || 'Invalid credentials')
        }
      }
    })

    return () => unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const credential = await firebaseLogin(email, password)
      await finalizeAdminSession(credential.user.uid)
    } catch (err: unknown) {
      const firebaseErr = err as {
        code?: string
        message?: string
        response?: { data?: { detail?: string } }
      }
      setError(
        firebaseErr.code === 'auth/invalid-credential'
          ? 'Invalid email or password'
          : apiErrorDetail(err) || firebaseErr.message || 'Invalid credentials',
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell
      tone="graphite"
      brandSubtitle="Admin Console"
      heroTitle={
        <>
          The platform,
          <br />
          at a glance<span className="text-highlight">.</span>
        </>
      }
      heroTagline="Manage customer onboarding, verification and platform operations from one workspace."
      valueProps={CONSOLE_HIGHLIGHTS}
      heroFootnote="Restricted to authorised LipaFlow platform administrators"
      cardFooter={
        <div className="space-y-2 text-center">
          <p className="font-inter text-body-sm text-ink-muted">
            Not LipaFlow staff?{' '}
            <a href={portalUrls.school} className="font-semibold text-accent hover:text-accent-hover">
              School Portal
            </a>
            {' · '}
            <a href={portalUrls.property} className="font-semibold text-accent hover:text-accent-hover">
              Property Portal
            </a>
          </p>
          <p className="font-inter text-caption text-ink-faint">
            Paying fees as a parent?{' '}
            <a href={portalUrls.parent} className="text-ink-muted underline hover:text-ink">
              Parent Portal &rarr;
            </a>
          </p>
        </div>
      }
    >
      <Card padding="lg" radius="panel">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-card bg-accent-soft">
            <ShieldCheckIcon className="h-6 w-6 text-accent" aria-hidden="true" />
          </div>
          <h2 className="font-outfit text-h2 text-ink">Admin login</h2>
          <p className="mt-1.5 font-inter text-body-sm text-ink-muted">
            Sign in with your super admin credentials.
          </p>
        </div>

        <AuthError message={error} />

        <form onSubmit={handleLogin} className="mt-6 space-y-4">
          <Input
            label="Email address"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            placeholder="admin@lipaflow.co.ke"
            required
          />

          <Input
            label="Password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            placeholder="Enter your password"
            required
            rightAdornment={
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="flex h-8 w-8 items-center justify-center rounded-chip text-ink-faint transition-colors duration-fast hover:bg-surface-alt hover:text-ink-muted focus-visible:outline-none focus-visible:shadow-focus"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            }
          />

          <div className="-mt-1 text-right">
            <Link
              to="/forgot-password"
              className="font-inter text-body-sm font-medium text-accent hover:text-accent-hover"
            >
              Forgot password?
            </Link>
          </div>

          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={loading}
            loadingText="Signing in…"
          >
            Sign in to Console
          </Button>
        </form>
      </Card>
    </AuthShell>
  )
}
